/*
  nazzi-hp-halftone.js
  Effetto firma della homepage: retino a linee diagonali in duotone verde,
  calcolato in WebGL a partire da una normale <img>. Nessuna libreria.

  Markup atteso:
    <div data-nazzi-halftone [data-period="6"] [data-shade="1"] [data-gain="1.3"] [data-focus="0.5,0.4"]>
      <img crossorigin="anonymous" ...>
      <canvas aria-hidden="true"></canvas>
    </div>

  Scelte di performance (richiesta esplicita: nessun lag):
  - niente loop perpetuo: si disegna solo all'ingresso a schermo (intro di
    1,4s) e al resize, poi il loop si ferma e la GPU resta a riposo. La lente
    che seguiva il mouse e stata tolta in v0.52.2 su richiesta di Antonio:
    l'effetto non gli piaceva e costava un ridisegno a ogni movimento;
  - l'avvio non passa mai dallo scroll (v0.51.0, misurato: l'avvio del
    secondo riquadro bloccava il main thread 20-70ms proprio mentre si
    scorreva). Il riquadro a schermo parte subito, gli altri nel primo
    momento di quiete dopo il load. L'avvio e diviso in tre passi: contesto
    e shader con compilazione parallela (KHR_parallel_shader_compile, senza
    attese sincrone verso la GPU), decodifica della foto fuori dal main
    thread con createImageBitmap, e solo alla fine upload e primo frame;
  - la texture e la stessa foto gia scaricata dalla pagina (nessun secondo
    download), un solo campionamento di texture per pixel;
  - DPR limitato a 2 e canvas limitato a 4 milioni di pixel sugli schermi
    grandi;
  - senza WebGL, o se il contesto si perde, resta il fallback CSS.
  I colori arrivano dalle custom property --nazzi-ht-dark e --nazzi-ht-light.
*/
(function () {
  'use strict';

  var VERT = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';

  var FRAG = [
    'precision mediump float;',
    'uniform sampler2D u_tex;',
    'uniform vec2 u_res;', // px fisici del canvas
    'uniform vec2 u_img;', // dimensioni naturali immagine
    'uniform vec2 u_focus;', // punto focale del cover (0..1)
    'uniform float u_dpr;',
    'uniform float u_period;', // passo delle linee in px CSS
    'uniform float u_reveal;', // 0..1 intro
    'uniform vec3 u_dark;',
    'uniform vec3 u_light;',
    'uniform float u_shade;', // 0..1 scurimento dove vive il testo
    'uniform float u_gain;', // guadagno di luminanza, per foto molto scure
    'float hash(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}',
    'void main(){',
    '  vec2 frag=vec2(gl_FragCoord.x,u_res.y-gl_FragCoord.y);',
    '  vec2 p=frag/u_dpr;', // px CSS
    '  vec2 size=u_res/u_dpr;',
    // uv in modalita cover
    '  float ra=size.x/size.y;float ri=u_img.x/u_img.y;',
    '  vec2 sc=ra>ri?vec2(1.,ri/ra):vec2(ra/ri,1.);',
    '  vec2 uv=(p/size-u_focus)*sc+u_focus;',
    '  float lum=dot(texture2D(u_tex,uv).rgb,vec3(.299,.587,.114));',
    '  lum=smoothstep(.06,.94,lum);',
    '  lum=pow(lum,.85);',
    '  lum=min(1.,lum*u_gain);',
    // scurimento morbido dove vive il testo
    '  vec2 n=p/size;',
    // Zona del testo quasi piena: su desktop la meta sinistra, su mobile tutto
    // cio che sta sotto la fascia alta dove si vede la foto. Qui le linee
    // restano un filo di trama e il titolo bianco ha contrasto pieno.
    '  float shWide=(1.-smoothstep(.34,.9,n.x))*(.8+.2*smoothstep(.0,.45,n.y));',
    '  float shTall=smoothstep(.05,.3,n.y);',
    '  float sh=mix(shWide,shTall,step(size.x,760.));',
    '  lum*=1.-u_shade*sh*.94;',
    // retino a linee a 45 gradi
    '  float t=(p.x+p.y)*.70710678/u_period;',
    '  float tri=abs(fract(t)-.5)*2.;',
    '  float w=clamp(lum,0.,1.)*.92*u_reveal;',
    '  float aa=1.6/(u_period*u_dpr);',
    '  float line=1.-smoothstep(w-aa,w+aa,tri);',
    '  vec3 col=mix(u_dark,u_light,line);',
    // leggero velo di luminanza sotto le linee, per dare profondita
    '  col=mix(col,u_light,lum*.10*u_reveal);',
    '  col+=(hash(frag)-.5)*.035;',
    '  gl_FragColor=vec4(col,1.);',
    '}'
  ].join('\n');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function cssColor(el, name, fallback) {
    var v = getComputedStyle(el).getPropertyValue(name).trim() || fallback;
    var m = /^#([0-9a-f]{6})$/i.exec(v);
    if (!m) m = /^#([0-9a-f]{6})$/i.exec(fallback);
    var n = parseInt(m[1], 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  var MAX_PIXELS = 4e6;

  // Esegue fn quando il main thread e libero, senza superare timeout ms
  function whenIdle(fn, timeout) {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: timeout || 2000 });
    else setTimeout(fn, 200);
  }

  function Halftone(root) {
    if (root.nazziHalftone) return root.nazziHalftone; // una sola istanza per riquadro
    root.nazziHalftone = this;
    this.root = root;
    this.img = root.querySelector('img');
    this.canvas = root.querySelector('canvas');
    this.period = parseFloat(root.getAttribute('data-period')) || 6;
    this.shade = parseFloat(root.getAttribute('data-shade')) || 0;
    this.gain = parseFloat(root.getAttribute('data-gain')) || 1;
    var focus = (root.getAttribute('data-focus') || '0.5,0.5').split(',');
    this.focus = [parseFloat(focus[0]), parseFloat(focus[1])];
    this.reveal = reduceMotion ? 1 : 0;
    this.raf = 0;
    this.started = 0;
    if (!this.img || !this.canvas) return;
    this.prepare();
  }

  // Passo 1: contesto e programma. Con KHR_parallel_shader_compile la
  // compilazione avviene in background e qui non si aspetta nulla.
  Halftone.prototype.prepare = function () {
    var opts = { alpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' };
    var gl = this.canvas.getContext('webgl', opts) || this.canvas.getContext('experimental-webgl', opts);
    if (!gl) return;
    this.gl = gl;
    this.glCanvas = this.canvas;
    this.parallel = gl.getExtension('KHR_parallel_shader_compile');

    var prog = gl.createProgram();
    [[gl.VERTEX_SHADER, VERT], [gl.FRAGMENT_SHADER, FRAG]].forEach(function (d) {
      var sh = gl.createShader(d[0]);
      gl.shaderSource(sh, d[1]);
      gl.compileShader(sh);
      gl.attachShader(prog, sh);
    });
    gl.linkProgram(prog);
    this.prog = prog;

    var self = this;
    this.canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      cancelAnimationFrame(self.raf);
      self.raf = 0;
      self.gl = null;
      self.root.classList.remove('is-ready');
    });

    // Passo 2: la foto va decodificata fuori dal main thread. Prima si
    // aspetta che sia scaricata, poi createImageBitmap la decodifica in
    // background (texImage2D su una <img> la decodificava in modo sincrono).
    var img = this.img;
    var decoded = new Promise(function (resolve, reject) {
      if (img.complete && img.naturalWidth) resolve();
      else {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', reject, { once: true });
      }
    }).then(function () {
      if ('createImageBitmap' in window) {
        return createImageBitmap(img, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' }).catch(function () { return img; });
      }
      return img.decode ? img.decode().then(function () { return img; }, function () { return img; }) : img;
    });
    decoded.then(function (source) {
      self.source = source;
      self.waitLink();
    }, function () {});
  };

  // Attende la fine della compilazione controllando a ogni frame, senza bloccare
  Halftone.prototype.waitLink = function () {
    var gl = this.gl;
    if (!gl) return;
    if (this.parallel && !gl.getProgramParameter(this.prog, this.parallel.COMPLETION_STATUS_KHR)) {
      var self = this;
      requestAnimationFrame(function () { self.waitLink(); });
      return;
    }
    this.setup();
  };

  // Passo 3: upload della foto gia decodificata, uniform e primo frame
  Halftone.prototype.setup = function () {
    var gl = this.gl;
    var prog = this.prog;
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, this.source);
    } catch (e) {
      return; // immagine non leggibile (CORS): resta il fallback CSS
    }
    var natW = this.source.width || this.img.naturalWidth;
    var natH = this.source.height || this.img.naturalHeight;
    if (this.source.close) this.source.close(); // la bitmap non serve piu, libera memoria
    this.source = null;

    var u = {};
    ['u_tex', 'u_res', 'u_img', 'u_focus', 'u_dpr', 'u_period', 'u_reveal', 'u_dark', 'u_light', 'u_shade', 'u_gain'].forEach(function (n) {
      u[n] = gl.getUniformLocation(prog, n);
    });
    this.u = u;
    gl.uniform1i(u.u_tex, 0);
    gl.uniform2f(u.u_img, natW, natH);
    gl.uniform2f(u.u_focus, this.focus[0], this.focus[1]);
    gl.uniform1f(u.u_period, this.period);
    gl.uniform1f(u.u_shade, this.shade);
    gl.uniform1f(u.u_gain, this.gain);
    var dark = cssColor(this.root, '--nazzi-ht-dark', '#072A20');
    var light = cssColor(this.root, '--nazzi-ht-light', '#3FA97F');
    gl.uniform3f(u.u_dark, dark[0], dark[1], dark[2]);
    gl.uniform3f(u.u_light, light[0], light[1], light[2]);

    var self = this;
    this.resize();
    if ('ResizeObserver' in window) {
      var pending = 0;
      new ResizeObserver(function () {
        cancelAnimationFrame(pending);
        pending = requestAnimationFrame(function () {
          if (self.resize()) self.kick();
        });
      }).observe(this.root);
    }

    // primo frame (linee chiuse), l'intro parte quando il riquadro e a schermo
    this.frame(performance.now());
    this.root.classList.add('is-ready');
    // Osservatore permanente: alla prima entrata parte l'intro, a ogni rientro
    // si ridisegna un frame (alcuni browser svuotano i canvas rimasti a lungo
    // fuori schermo o in una scheda in background).
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        if (!self.started) self.started = performance.now();
        self.kick();
      }, { threshold: 0.15 }).observe(this.root);
    } else {
      this.reveal = 1;
      this.kick();
    }
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) self.kick();
    });
  };

  Halftone.prototype.resize = function () {
    var cw = this.root.clientWidth;
    var ch = this.root.clientHeight;
    var dpr = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(MAX_PIXELS / Math.max(1, cw * ch)));
    var w = Math.round(cw * dpr);
    var h = Math.round(ch * dpr);
    if (!w || !h || !this.gl || !this.u) return false;
    if (this.sized && this.glCanvas.width === w && this.glCanvas.height === h) return false;
    this.sized = true;
    this.glCanvas.width = w;
    this.glCanvas.height = h;
    this.dpr = dpr;
    this.gl.viewport(0, 0, w, h);
    this.gl.uniform2f(this.u.u_res, w, h);
    this.gl.uniform1f(this.u.u_dpr, dpr);
    return true;
  };

  Halftone.prototype.kick = function () {
    if (!this.raf && this.gl) this.raf = requestAnimationFrame(this.frame.bind(this));
  };

  Halftone.prototype.frame = function (now) {
    this.raf = 0;
    var gl = this.gl;
    var again = false;
    if (!gl || !this.u) return;

    if (this.reveal < 1 && this.started) {
      var t = Math.min(1, (now - this.started) / 1400);
      this.reveal = 1 - Math.pow(1 - t, 3);
      if (t < 1) again = true;
      else this.reveal = 1;
    }
    gl.uniform1f(this.u.u_reveal, this.reveal);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    if (again) this.kick();
  };

  function start(n) {
    if (!n.nazziHalftone) new Halftone(n);
  }

  function boot() {
    var nodes = [].slice.call(document.querySelectorAll('[data-nazzi-halftone]'));
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(start);
      return;
    }
    // La prima risposta dell'IntersectionObserver dice quali riquadri sono a
    // schermo senza forzare il layout: getBoundingClientRect impaginerebbe
    // anche le sezioni rimandate con content-visibility. Quelli visibili
    // (l'hero) partono subito, gli altri vanno in coda.
    var later = [];
    var seen = 0;
    var first = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        first.unobserve(en.target);
        seen++;
        if (en.isIntersecting) start(en.target);
        else later.push(en.target);
      });
      if (seen === nodes.length && later.length) queueLater(later);
    });
    nodes.forEach(function (n) { first.observe(n); });
  }

  // Gli altri: uno per volta nei momenti di quiete dopo il load, cosi il
  // lavoro e gia fatto quando lo scroll ci arriva. Se l'utente scorre
  // velocissimo prima, l'osservatore li avvia comunque con largo anticipo.
  function queueLater(later) {
    var queue = later.slice();
    function next() {
      var n = queue.shift();
      if (!n) return;
      whenIdle(function () {
        start(n);
        next();
      }, 3000);
    }
    if (document.readyState === 'complete') next();
    else window.addEventListener('load', next, { once: true });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        start(en.target);
      });
    }, { rootMargin: '1200px 0px' });
    later.forEach(function (n) { io.observe(n); });
  }

  // Esposto per riusare l'effetto altrove (new NazziHalftone(elemento))
  window.NazziHalftone = Halftone;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
